import React from 'react';

import { Modal, Button, Title, Group, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { AxiosError, AxiosResponse } from 'axios';
import AthleteStatusSelect, {
  athleteStatusOptions,
} from 'components/form-fields/select-fields/AthleteStatusSelect';
import { UseMutationResult } from 'react-query';
import {
  AthletePackage,
  AthletePackageRetrieve,
  AthletePackageUpdate,
  AthletePackageUpdateResponse,
  ErrorMessage,
} from 'types';
import notificationHandler from 'utils/notifications';

interface IChangeAthleteStatusesModalProps {
  opened: boolean;
  athletePackage: AthletePackageRetrieve;
  closeHandler: () => void;
  updateAthletePackageMutation: UseMutationResult<
    AxiosResponse<AthletePackageUpdateResponse>,
    AxiosError<ErrorMessage>,
    AthletePackageUpdate
  >;
}

interface FormValues {
  athleteStatusOnStart: Exclude<AthletePackage['athleteStatusOnStart'], null> | 'No status change';
  athleteStatusOnEnd: Exclude<AthletePackage['athleteStatusOnEnd'], null> | 'No status change';
}

const ChangeAthleteStatusesModal = ({
  opened,
  athletePackage,
  closeHandler,
  updateAthletePackageMutation,
}: IChangeAthleteStatusesModalProps): JSX.Element => {
  const { successNotification, errorNotification } = notificationHandler();

  const form = useForm<FormValues>({
    initialValues: {
      athleteStatusOnStart: athletePackage.athleteStatusOnStart || 'No status change',
      athleteStatusOnEnd: athletePackage.athleteStatusOnEnd || 'No status change',
    },
    validate: {
      athleteStatusOnStart: value =>
        athleteStatusOptions.onStart.includes(value) ? null : 'Please select a valid status',
      athleteStatusOnEnd: value =>
        athleteStatusOptions.onEnd.includes(value) ? null : 'Please select a valid status',
    },
  });

  const handleSubmit = (formValues: FormValues): void => {
    const payload: AthletePackageUpdate = {
      action: 'athlete_statuses',
      athletePackageId: athletePackage.athletePackageId,
      autoRenew: athletePackage.autoRenew,
      autoPay: athletePackage.autoPay,
      paymentMethod: athletePackage.paymentMethod,
      nonStripePaymentMethod: athletePackage.nonStripePaymentMethod,
      athleteStatusOnStart:
        formValues.athleteStatusOnStart === 'No status change' ? null : formValues.athleteStatusOnStart,
      athleteStatusOnEnd:
        formValues.athleteStatusOnEnd === 'No status change' ? null : formValues.athleteStatusOnEnd,
    };
    updateAthletePackageMutation.mutate(payload, {
      onError: error => {
        errorNotification('Athlete package update failed', error.response?.data.message);
      },
      onSuccess: () => {
        successNotification('Athlete package update success', `Athlete package successfully updated.`);
        closeHandler();
      },
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={closeHandler}
      size='md'
      centered
      title={
        <Title order={4} pr='md'>
          Manage Status Changes
        </Title>
      }
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack p='md'>
          <AthleteStatusSelect
            {...form.getInputProps('athleteStatusOnStart')}
            label='Athlete Status Upon Start'
            description='Occurs on package start date if not a renewal'
            type='onStart'
            disabled={athletePackage.status !== 'UPCOMING' || !!athletePackage.parent}
            withinPortal={true}
          />
          <AthleteStatusSelect
            {...form.getInputProps('athleteStatusOnEnd')}
            label='Athlete Status Upon End'
            description='Occurs on package end date if not renewed'
            type='onEnd'
            excludeValue={form.values.athleteStatusOnStart}
            withinPortal={true}
          />
        </Stack>
        <Group position='right' pt='md'>
          <Button
            variant='light'
            onClick={closeHandler}
            size='xs'
            radius='md'
            disabled={updateAthletePackageMutation.isLoading}
          >
            Cancel
          </Button>
          <Button size='xs' radius='md' type='submit' loading={updateAthletePackageMutation.isLoading}>
            Save
          </Button>
        </Group>
      </form>
    </Modal>
  );
};

export default ChangeAthleteStatusesModal;
